import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import BrochureGate from "@/components/BrochureGate";
import { 
  TreePine, CheckCircle, Award, Shield, Calendar, 
  Phone, Mail, MapPin, Star, ArrowRight, Download,
  Clock, Users, Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";

export default function GolfCoursePage() {
  const [scrolled, setScrolled] = useState(false);
  const [showBrochureGate, setShowBrochureGate] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const membershipTiers = [
    {
      name: "Seasonal Partnership",
      duration: "6 Months",
      features: [
        "Bi-weekly course inspections",
        "Fairway tree trimming & shaping",
        "Hazard tree identification",
        "Pre-tournament preparation",
        "Dedicated account manager",
        "Priority storm response",
      ],
      highlighted: false,
    },
    {
      name: "Championship Excellence",
      duration: "12 Months",
      features: [
        "Weekly property visits",
        "Complete tree & shrub maintenance",
        "Fire safety compliance program",
        "24/7 priority emergency service",
        "Quarterly ISA Arborist assessments",
        "Tournament-ready preparation",
        "Irrigation zone tree care",
        "Cart path clearance maintenance",
        "Dedicated landscape coordinator",
      ],
      highlighted: true,
      badge: "Most Popular",
    },
    {
      name: "Elite Course Care",
      duration: "Multi-Year",
      features: [
        "All Championship Excellence features",
        "Annual tree health mapping",
        "Custom landscape planning",
        "Locked-in pricing protection",
        "Exclusive scheduling priority",
        "Complimentary staff training",
        "Quarterly executive reviews",
      ],
      highlighted: false,
      badge: "Best Value",
    },
  ];

  const serviceAreas = [
    { name: "Scottsdale", zips: ["85250", "85251", "85254", "85255", "85258", "85259", "85260", "85262"] },
    { name: "Paradise Valley", zips: ["85253"] },
    { name: "Phoenix - Arcadia", zips: ["85018", "85016"] },
    { name: "Phoenix - Biltmore", zips: ["85016", "85014"] },
    { name: "Fountain Hills", zips: ["85268", "85269"] },
    { name: "Cave Creek", zips: ["85331"] },
    { name: "Carefree", zips: ["85377"] },
  ];

  const premierCourses = [
    "The Phoenician Golf Course",
    "Troon North Golf Club",
    "Grayhawk Golf Club",
    "TPC Scottsdale",
    "Gainey Ranch Golf Club",
    "Desert Mountain Club",
    "Silverleaf Club",
    "Estancia Club",
  ];

  return (
    <>
      <Helmet>
        <title>Golf Course Tree Care & Landscaping | Celebrity Tree Service | Scottsdale, Paradise Valley, Phoenix AZ</title>
        <meta name="description" content="Premium golf course tree care and landscaping services in Scottsdale, Paradise Valley, and Phoenix Arizona. ISA Certified Arborists. Championship-level maintenance for Arizona's finest courses." />
        <meta name="keywords" content="golf course tree care, golf course landscaping, Scottsdale golf course maintenance, Paradise Valley tree service, Phoenix golf course trees, ISA certified arborist golf course" />
        <link rel="canonical" href="https://paradisevalleytreeservice.com/golf-courses" />
      </Helmet>

      <div className="min-h-screen bg-[#F9F9F5]" data-testid="golf-course-page">
        <Navigation scrolled={scrolled} />

        {/* Hero Section */}
        <section 
          className="relative min-h-[70vh] flex items-center pt-32"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/164250/pexels-photo-164250.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/70 to-transparent" />
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-[#10B981]/20 border border-[#10B981]/40 px-4 py-2 mb-6">
                <TreePine className="w-4 h-4 text-[#10B981]" />
                <span className="text-[#10B981] text-sm font-semibold uppercase tracking-wider">
                  Golf Course Specialists
                </span>
              </div>
              
              <h1 className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                Championship-Level
                <br />
                <span className="text-[#D4A853]">Tree Care</span>
              </h1>
              
              <p className="text-white/80 text-xl mb-8 max-w-2xl">
                Arizona's premier golf courses trust Celebrity Tree Service for course-ready 
                landscaping. ISA Certified Arborists ensuring your course maintains its 
                world-class reputation.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => setShowBrochureGate(true)}
                  className="bg-gradient-to-r from-[#D4A853] to-[#B8923A] text-[#0A1628] rounded-none px-8 py-6 uppercase tracking-[0.15em] text-sm font-bold"
                  data-testid="download-brochure-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Brochure
                </Button>
                <Button
                  asChild
                  className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-none px-8 py-6 uppercase tracking-[0.15em] text-sm font-bold"
                >
                  <a href="tel:4803517775">Call (480) 351-7775</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="bg-[#0A1628] py-8 border-y border-[#10B981]/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { icon: Award, label: "ISA Certified", sub: "Arborists" },
                { icon: Shield, label: "Fully Insured", sub: "$5M Coverage" },
                { icon: Clock, label: "24/7 Emergency", sub: "Response" },
                { icon: Users, label: "Since 2015", sub: "Trusted Partner" },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center justify-center gap-3">
                  <item.icon className="w-8 h-8 text-[#10B981]" />
                  <div className="text-left">
                    <p className="text-white font-bold">{item.label}</p>
                    <p className="text-white/60 text-sm">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Membership Tiers */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase mb-4">
                Partnership Programs
              </p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[#0A1628] mb-6">
                Golf Course Memberships
              </h2>
              <p className="text-[#64748B] max-w-2xl mx-auto text-lg">
                Tailored maintenance programs designed for Arizona's premier golf courses. 
                Each partnership includes dedicated account management and priority scheduling.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {membershipTiers.map((tier, idx) => (
                <div 
                  key={idx}
                  className={`relative p-8 ${
                    tier.highlighted 
                      ? "bg-gradient-to-br from-[#0A1628] to-[#1E3A5F] text-white border-2 border-[#10B981]"
                      : "bg-[#F8F6F1] border border-[#10B981]/20"
                  }`}
                  data-testid={`membership-tier-${idx}`}
                >
                  {tier.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4A853] text-[#0A1628] px-4 py-1 text-xs font-bold uppercase tracking-wider">
                      {tier.badge}
                    </div>
                  )}
                  
                  <div className="mb-6">
                    <p className={`text-sm font-semibold uppercase tracking-wider mb-2 ${tier.highlighted ? "text-[#10B981]" : "text-[#10B981]"}`}>
                      {tier.duration}
                    </p>
                    <h3 className={`font-['Cormorant_Garamond'] text-2xl font-bold ${tier.highlighted ? "text-white" : "text-[#0A1628]"}`}>
                      {tier.name}
                    </h3>
                  </div>
                  
                  <div className="space-y-3 mb-8">
                    {tier.features.map((feature, fidx) => (
                      <div key={fidx} className="flex items-start gap-3">
                        <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${tier.highlighted ? "text-[#10B981]" : "text-[#10B981]"}`} />
                        <span className={tier.highlighted ? "text-white/90" : "text-[#64748B]"}>{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button
                    asChild
                    className={`w-full rounded-none py-5 uppercase tracking-[0.15em] text-sm font-bold ${
                      tier.highlighted 
                        ? "bg-gradient-to-r from-[#10B981] to-[#059669] text-white"
                        : "bg-[#0A1628] text-white hover:bg-[#1E3A5F]"
                    }`}
                  >
                    <a href="/#contact">Request Custom Quote</a>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Uniform Customization Notice */}
        <section className="py-16 bg-gradient-to-r from-[#D4A853]/10 to-[#D4A853]/5">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Sparkles className="w-10 h-10 text-[#D4A853] mx-auto mb-4" />
            <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#0A1628] mb-4">
              White-Glove Service Experience
            </h3>
            <p className="text-[#64748B] text-lg mb-4">
              For our golf course partners, our team can wear custom uniforms featuring your course's 
              logo and branding. This premium service ensures our crew blends seamlessly with your 
              property's professional image.
            </p>
            <p className="text-[#0A1628] font-semibold">
              <span className="text-[#D4A853]">Note:</span> Custom uniform costs are the responsibility of the course 
              as part of the partnership setup process.
            </p>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-24 bg-[#FDFCF9]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase mb-4">
                Service Coverage
              </p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[#0A1628] mb-6">
                Premium Service Areas
              </h2>
              <p className="text-[#64748B] max-w-2xl mx-auto">
                Serving Arizona's most prestigious golf communities within 15 miles of 
                Scottsdale and Paradise Valley. Also available in select California locations.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {serviceAreas.map((area, idx) => (
                <div key={idx} className="bg-white p-6 border border-[#10B981]/20">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-5 h-5 text-[#10B981]" />
                    <h3 className="font-bold text-[#0A1628]">{area.name}</h3>
                  </div>
                  <p className="text-[#64748B] text-sm">
                    Zip Codes: {area.zips.join(", ")}
                  </p>
                </div>
              ))}
            </div>

            {/* California Expansion */}
            <div className="bg-gradient-to-r from-[#0A1628] to-[#1E3A5F] p-8 text-center">
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-white mb-4">
                Now Serving California
              </h3>
              <p className="text-white/80 mb-6 max-w-2xl mx-auto">
                Own properties in California? Celebrity Tree Service now offers our premium 
                golf course care in select California locations. Contact us for availability.
              </p>
              <Button
                asChild
                className="bg-gradient-to-r from-[#D4A853] to-[#B8923A] text-[#0A1628] rounded-none px-8 py-4 uppercase tracking-[0.15em] text-sm font-bold"
              >
                <a href="tel:4803517775">Inquire About California Service</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Trusted By Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center font-['Cinzel'] text-[#64748B] text-xs tracking-[0.3em] uppercase mb-8">
              Trusted by Arizona's Premier Golf Courses
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {premierCourses.map((course, idx) => (
                <div key={idx} className="text-center py-4 px-6 bg-[#F8F6F1]">
                  <p className="font-['Cormorant_Garamond'] text-lg font-semibold text-[#0A1628]">{course}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-br from-[#0A1628] to-[#1E3A5F]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to Elevate Your Course?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
              Join Arizona's most prestigious courses in partnering with Celebrity Tree Service. 
              Download our comprehensive brochure or speak with our team today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => setShowBrochureGate(true)}
                className="bg-gradient-to-r from-[#D4A853] to-[#B8923A] text-[#0A1628] rounded-none px-10 py-6 uppercase tracking-[0.15em] text-sm font-bold"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Golf Course Brochure
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0A1628] rounded-none px-10 py-6 uppercase tracking-[0.15em] text-sm font-bold"
              >
                <a href="/#contact">Schedule Consultation</a>
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>

      {/* Brochure Gate Modal */}
      {showBrochureGate && (
        <BrochureGate 
          type="golf-course" 
          onClose={() => setShowBrochureGate(false)} 
        />
      )}
    </>
  );
}
